import { UNIT_TRANSLATE } from 'constants/translate'
import { ELegendType } from 'enums/common'
import { EKPIUnit } from 'enums/kpi'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  LegendProps,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts'
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent'
import { translateValueMetric } from 'utils/common'
import NumberUtil from 'utils/number'
import { CustomLabel } from './components/CustomLable'
import { renderLineLegend } from './components/LineChartLegend'

export interface RechartsProps {
  key: string
  yAxisId?: string
  color: string
  display: boolean
  isHover: boolean
}

interface BaseAreaChartProps<T> {
  data: T[]
  colors: any
  isLineChart: boolean
  chartProps: Array<RechartsProps>
  yAxisProps?: Array<{
    yAxisId: string
    orientation: 'left' | 'right'
    unit: string
  }>
  width?: string | number
  height?: string | number
  w?: number
  metric: string[]
  unit: string
  margin?: {
    top?: number
    bottom?: number
    right?: number
    left?: number
  }
  legendProps?: Pick<
    LegendProps,
    'height' | 'width' | 'layout' | 'align' | 'margin' | 'formatter' | 'iconType' | 'iconSize' | 'onClick'
  >
}

const filterObjectByKeys = (a: Array<RechartsProps>, keys: string[]) => {
  const keysFormat: string[] = keys
    ? (keys.map((key) => {
        if (key === 'ActualPerformance') {
          return ELegendType.ACTUAL_PERFORM
        }
        if (key === 'Baseline') {
          return ELegendType.BASELINE
        }
        if (key === 'Target') {
          return ELegendType.TARGET
        }
      }) as string[])
    : []

  return a?.reduce((acc: Array<RechartsProps>, key: RechartsProps) => {
    if (keysFormat?.includes(key?.key)) {
      acc?.push(key)
    }
    return acc
  }, [])
}

function BaseAreaChart<T>({
  data,
  colors,
  width = '100%',
  height = '100%',
  w,
  metric,
  unit,
  isLineChart,
  chartProps
}: BaseAreaChartProps<T>) {
  const formatChartProps = filterObjectByKeys(chartProps, metric)

  const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length > 0) {
      return (
        <div className='border-none bg-black-nomal text-white py-1.5 px-2 rounded'>
          {payload.map((entry, index) => (
            <div key={index}>
              <span>{translateValueMetric(entry.name as string)}: </span>
              <span>{NumberUtil.format(entry.value as number, { unit: unit as EKPIUnit })}</span>
            </div>
          ))}
        </div>
      )
    }

    return null
  }

  return (
    <ResponsiveContainer width={width} height={height}>
      <AreaChart data={data} margin={{ top: 30, right: 15, left: 10, bottom: w && w < 10 ? 40 : 0 }}>
        <defs>
          {formatChartProps.map((cp, i) => (
            <linearGradient key={cp.key + i} id={`color${cp.key}`} x1='0' y1='0' x2='0' y2='1'>
              <stop offset='5%' stopColor={colors?.[cp.key]} stopOpacity={0.8} />
              <stop offset='95%' stopColor={colors?.[cp.key]} stopOpacity={0.05} />
            </linearGradient>
          ))}
        </defs>
        <XAxis
          dataKey='name'
          type='category'
          interval={w && w > 10 ? 0 : undefined}
          padding={{ left: 20, right: 20 }}
        />
        <YAxis />
        <CartesianGrid strokeDasharray='3 3' />
        <Tooltip content={<CustomTooltip />} />
        <Legend content={renderLineLegend} />
        {formatChartProps.map((cp, i) => (
          <Area
            key={i}
            stackId={isLineChart ? i : 1}
            dataKey={cp.key}
            stroke={colors?.[cp.key]}
            strokeWidth={2}
            fill={isLineChart ? 'transparent' : `url(#color${cp.key})`}
          />
        ))}
        <ReferenceLine
          x={(data as any)?.[0]?.name}
          label={(e) => CustomLabel({ ...e, title: `(${UNIT_TRANSLATE[unit as EKPIUnit] || '_'})`, dy: 20, dx: 35 })}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default BaseAreaChart
